import { styled } from "goober";
import { Fragment, useState, type ReactNode } from "react";
import {
  connectorUsageReportCounts,
  type ConnectorUsageReportType,
} from "../../../shared/connectorUsageReporting";
import {
  getAIProvider,
  resolveAIProviderId,
} from "../../../shared/aiProviders";
import type { MeteredMeasure } from "../../../shared/connectors/types";
import { queries } from "../../../shared/queries";
import { usePersonalWorkEnabled } from "../../hooks/usePersonalWorkEnabled";
import { useQuery } from "@rocicorp/zero/react";
import {
  groupByWorkflow,
  usageTypeIcons,
  type WorkflowUsage,
} from "../../lib/connector";
import { useFormatRelativeTime } from "../../hooks/useFormatRelativeTime";
import { useMakeUrl, routes } from "../../lib/urls";
import { closeModalStack } from "../globalModalStack";
import { CircleMesh } from "../CircleMesh";
import { parseBadgeConfig } from "../CircleMeshEditor";
import { CountBadge } from "../CountBadge";
import { Dropdown } from "../Dropdown";
import { EmptyState } from "../EmptyState";
import { Icon } from "../Icon";
import { RedactedScopeRow } from "../RedactedScopeRow";
import { SpaceBadge } from "../SpaceBadge";
import { Text } from "../Text";
import { WavyLine } from "../WavyLine";
import { MANAGED_AI_CONNECTOR_ID } from "../../../shared/aiProviders/managedAiConnector";
import { ManagedAiPricingSection } from "./ManagedAiPricingSection";
import { MeasurePricingSection } from "./MeasurePricingSection";
import { UsageLimitsSection } from "./UsageLimitsSection";
import {
  BadgeWrapper,
  BranchControl,
  BranchLabel,
  LoadingContainer,
  StepColor,
  StepInfoContainer,
  StepList,
  StepNameRow,
  StepRow,
  WorkflowGroup,
  WorkflowHeader,
  WorkflowSection,
} from "./usageList";

function branchLabel(name: string): string {
  return name === "main" ? "Live" : name;
}

type Props = {
  connectedAppId: string;
  loading: boolean;
  usageMeasures: readonly MeteredMeasure[] | null;
  managedAi: boolean;
};

export function UsageTab({
  connectedAppId,
  loading,
  usageMeasures,
  managedAi,
}: Props) {
  const makeUrl = useMakeUrl();
  const personalWorkEnabled = usePersonalWorkEnabled();
  const [usageTotals, totalsResult] = useQuery(
    queries.connectorUsageTotals({ connectedAppId })
  );
  const [usageRows, usageResult] = useQuery(
    queries.connectorUsageDetails({ connectedAppId })
  );
  const [personalChatUsage, chatResult] = useQuery(
    queries.loadConnectedAppPersonalChatUsage({ connectedAppId })
  );
  const [gitRemoteRows, gitResult] = useQuery(
    queries.loadConnectedAppGitRemoteUsage({ connectedAppId })
  );
  const [auditRows, auditResult] = useQuery(
    queries.loadConnectedAppAuditUsage({ connectedAppId })
  );
  const [aiProviderRows, aiResult] = useQuery(
    queries.loadConnectedAppAiProviderUsage({ connectedAppId })
  );
  const [skillSourceRows, skillSourceResult] = useQuery(
    queries.loadConnectedAppSkillSourceUsage({ connectedAppId })
  );
  const [meteredDimensions, meteredDimensionsResult] = useQuery(
    queries.connectorUsageDimensions({ connectedAppId })
  );

  const totals = connectorUsageReportCounts(
    "details",
    usageTotals?.usages ?? []
  );
  const groups = groupByWorkflow(usageRows);
  const visibleSteps = new Set(
    usageRows.flatMap((row) =>
      row.connectorUsages.flatMap((usage) =>
        usage.stepDirs.map((step) => `${usage.branchId}/${step}`)
      )
    )
  );
  const hiddenStepCount = (usageTotals?.currentUses ?? [])
    .flatMap((usage) =>
      usage.stepDirs.map((step) => `${usage.branchId}/${step}`)
    )
    .filter((step) => !visibleSteps.has(step)).length;
  const hiddenChatCount = Math.max(
    0,
    totals.personal_chat - personalChatUsage.length
  );
  const hiddenGitCount = Math.max(
    0,
    totals.space_git_remote - gitRemoteRows.length
  );
  const hiddenSkillSourceCount = Math.max(
    0,
    totals.skill_git_source - skillSourceRows.length
  );
  const hiddenAuditCount = Math.max(
    0,
    totals.audit_log_destination - auditRows.length
  );
  const hiddenAiCount = Math.max(0, totals.ai_provider - aiProviderRows.length);

  const hasWorkflows = groups.length > 0 || hiddenStepCount > 0;
  const hasChats =
    personalWorkEnabled === true &&
    (personalChatUsage.length > 0 || hiddenChatCount > 0);
  const hasGitRemotes = gitRemoteRows.length > 0 || hiddenGitCount > 0;
  const hasSkillSources =
    skillSourceRows.length > 0 || hiddenSkillSourceCount > 0;
  const hasAudit = auditRows.length > 0 || hiddenAuditCount > 0;
  const hasAiProviders = aiProviderRows.length > 0 || hiddenAiCount > 0;

  const sections = {
    personal_chat: {
      loading: chatResult?.type !== "complete",
      content: hasChats && (
        <UsageListSection
          iconName={usageTypeIcons.personal_chat}
          title="Chats"
          rows={personalChatUsage}
          hiddenCount={hiddenChatCount}
          scopeKind="chat"
          renderRow={(row) => {
            const pc = row.personalChat;
            if (!pc) return null;
            return {
              key: pc.id,
              url: makeUrl(routes.chats, {
                tabId: pc.id,
                userId: pc.userId,
                ...closeModalStack(),
              }),
              primary: (
                <Text variant="text-regular-md" ellipsis>
                  {pc.name}
                </Text>
              ),
              secondary: pc.user?.name,
            };
          }}
        />
      ),
    },
    space_git_remote: {
      loading: gitResult?.type !== "complete",
      content: hasGitRemotes && (
        <UsageListSection
          iconName={usageTypeIcons.space_git_remote}
          title="Git sync"
          rows={gitRemoteRows}
          hiddenCount={hiddenGitCount}
          scopeKind="git remote"
          renderRow={(row) => {
            const space = row.space;
            if (!space) return null;
            return {
              key: row.refId,
              url: makeUrl(routes.modal.space, { id: space.id }),
              primary: (
                <Text variant="text-regular-md" ellipsis>
                  {space.name}
                </Text>
              ),
              secondary: row.spaceGitRemote?.remoteUrl,
            };
          }}
        />
      ),
    },
    skill_git_source: {
      loading: skillSourceResult?.type !== "complete",
      content: hasSkillSources && (
        <UsageListSection
          iconName={usageTypeIcons.skill_git_source}
          title="Skill sources"
          rows={skillSourceRows}
          hiddenCount={hiddenSkillSourceCount}
          scopeKind="skill source"
          renderRow={(row) => ({
            key: row.id,
            url: makeUrl(routes.skills.list, { ...closeModalStack() }),
            primary: (
              <Text variant="text-regular-md" ellipsis>
                {row.remoteUrl}
              </Text>
            ),
            secondary: row.ref ?? undefined,
          })}
        />
      ),
    },
    audit_log_destination: {
      loading: auditResult?.type !== "complete",
      content: hasAudit && (
        <UsageListSection
          iconName={usageTypeIcons.audit_log_destination}
          title="Audit log destinations"
          rows={auditRows}
          hiddenCount={hiddenAuditCount}
          scopeKind="audit log destination"
          renderRow={(row) => {
            const dest = row.auditLogDestination;
            if (!dest) return null;
            return {
              key: row.refId,
              url: makeUrl(routes.settings.auditLogs.destinations, {
                destinationId: dest.id,
                ...closeModalStack(),
              }),
              primary: (
                <Text variant="text-regular-md" ellipsis>
                  {dest.name}
                </Text>
              ),
              secondary: dest.url,
            };
          }}
        />
      ),
    },
    ai_provider: {
      loading: aiResult?.type !== "complete",
      content: hasAiProviders && (
        <UsageListSection
          iconName={usageTypeIcons.ai_provider}
          title="AI providers"
          rows={aiProviderRows}
          hiddenCount={hiddenAiCount}
          scopeKind="AI provider"
          renderRow={(row) => {
            const provider = row.aiProvider;
            if (!provider) return null;
            return {
              key: row.refId,
              url: makeUrl(routes.settings.ai, {
                configId: provider.id,
                ...closeModalStack(),
              }),
              primary: (
                <Text variant="text-regular-md" ellipsis>
                  {getAIProvider(resolveAIProviderId(provider)).label}
                </Text>
              ),
              secondary: provider.baseUrl ?? undefined,
            };
          }}
        />
      ),
    },
  } satisfies Record<
    ConnectorUsageReportType<"details">,
    { loading: boolean; content: ReactNode }
  >;

  const stillLoading =
    loading ||
    totalsResult?.type !== "complete" ||
    usageResult?.type !== "complete" ||
    Object.values(sections).some((section) => section.loading) ||
    meteredDimensionsResult?.type !== "complete";

  if (stillLoading) {
    return (
      <LoadingContainer>
        <WavyLine />
      </LoadingContainer>
    );
  }

  if (
    !usageMeasures &&
    !managedAi &&
    !hasWorkflows &&
    !Object.values(sections).some((section) => section.content)
  ) {
    return (
      <EmptyState
        iconName="plug"
        title="Nothing is using this connector"
        description={
          personalWorkEnabled
            ? "Steps, chats, git sync, skill sources, and audit log destinations that authenticate with this connector show up here."
            : "Steps, git sync, skill sources, and audit log destinations that authenticate with this connector show up here."
        }
      />
    );
  }

  return (
    <Container>
      {/* 3B prices managed models and meters every managed AI connector
          and chat against one tenant pool, so this connector shows that
          pool’s rates and limits rather than its own. */}
      {managedAi && (
        <>
          <ManagedAiPricingSection />
          <UsageLimitsSection connectorId={MANAGED_AI_CONNECTOR_ID} />
        </>
      )}
      {usageMeasures && (
        <>
          <MeasurePricingSection
            connectorId={connectedAppId}
            meteredDimensions={meteredDimensions}
            measures={usageMeasures}
          />
          <UsageLimitsSection connectorId={connectedAppId} />
        </>
      )}
      {hasWorkflows && (
        <WorkflowSection>
          {groups.map((group) => (
            <WorkflowUsageGroup key={group.workflowId} group={group} />
          ))}
          {hiddenStepCount > 0 && (
            <StepList>
              <RedactedScopeRow count={hiddenStepCount} scopeKind="step" />
            </StepList>
          )}
        </WorkflowSection>
      )}
      {Object.entries(sections).map(([type, section]) => (
        <Fragment key={type}>{section.content}</Fragment>
      ))}
    </Container>
  );
}

function WorkflowUsageGroup({ group }: { group: WorkflowUsage }) {
  const makeUrl = useMakeUrl();
  const formatRelativeTime = useFormatRelativeTime();
  const [selectedBranchId, setSelectedBranchId] = useState(
    group.usageBranches[0]?.branchId
  );
  const selected =
    group.usageBranches.find((b) => b.branchId === selectedBranchId) ??
    group.usageBranches[0];

  const counterMap = new Map<
    string,
    { count: number; lastRunAt: number | null }
  >();
  for (const c of selected?.stepActivity ?? [])
    counterMap.set(c.stepDir, {
      count: c.executionCount ?? 0,
      lastRunAt: c.latestCreatedAt ?? null,
    });

  const badgeConfig = parseBadgeConfig(group.badgeSeed, group.workflowId);
  const hasLiveBranch = group.branches.some((b) => b.name === "main");

  if (!selected) return null;

  return (
    <WorkflowGroup>
      <WorkflowHeader>
        <BadgeWrapper style={hasLiveBranch ? undefined : { opacity: 0.5 }}>
          <CircleMesh
            key={group.badgeSeed}
            targetCircleSize={8}
            seed={badgeConfig.seed}
            mutingIntensity={0}
            animate={false}
            restrictedColors={badgeConfig.colors}
          />
        </BadgeWrapper>
        <Text variant="text-regular-md" color="var(--text-primary)" ellipsis>
          {group.workflowName}
        </Text>
        <SpaceBadge space={group.space} />
        <BranchControl>
          {group.usageBranches.length > 1 ? (
            <Dropdown
              align="bottom"
              minWidth={180}
              opener={
                <BranchPickerOpener>
                  <Icon
                    name="git-branch"
                    size={12}
                    color="var(--text-secondary)"
                  />
                  <Text
                    variant="text-small"
                    color="var(--text-secondary)"
                    ellipsis
                  >
                    {branchLabel(selected.branchName)}
                  </Text>
                  <Icon
                    name="chevron-down"
                    size={12}
                    color="var(--text-secondary)"
                  />
                </BranchPickerOpener>
              }
              sections={[
                {
                  items: group.usageBranches.map((b) => ({
                    type: "button" as const,
                    key: b.branchId,
                    left: (
                      <Icon
                        name="git-branch"
                        size={16}
                        color="var(--text-secondary)"
                      />
                    ),
                    label: branchLabel(b.branchName),
                    selected: b.branchId === selected.branchId,
                    right:
                      b.branchId === selected.branchId ? (
                        <Icon name="check" size={12} />
                      ) : undefined,
                    onClick: () => setSelectedBranchId(b.branchId),
                  })),
                },
              ]}
            />
          ) : (
            <BranchLabel>
              <Icon name="git-branch" size={12} color="var(--text-secondary)" />
              <Text variant="text-small" color="var(--text-secondary)" ellipsis>
                {branchLabel(selected.branchName)}
              </Text>
            </BranchLabel>
          )}
        </BranchControl>
      </WorkflowHeader>

      <StepList>
        {selected.steps.map((step) => {
          const url = makeUrl(routes.space.workflow, {
            spaceId: step.spaceId,
            workflowId: step.workflowId,
            step: step.stepDir,
            ...closeModalStack(),
          });
          const entry = counterMap.get(step.stepDir);
          const count = entry?.count ?? 0;
          const lastRun = entry?.lastRunAt ?? null;
          return (
            <StepRow key={step.stepDir} to={url}>
              <StepColor />
              <StepInfoContainer>
                <StepNameRow>
                  <Text variant="text-regular-md" ellipsis>
                    {step.stepDir}
                  </Text>
                  <CountBadge
                    style={{
                      padding: "1px 6px",
                      background: "var(--alpha4)",
                      borderRadius: 8,
                      flexShrink: 0,
                    }}
                  >
                    <Text variant="text-small-md" color="var(--text-secondary)">
                      {count.toLocaleString()}
                    </Text>
                  </CountBadge>
                </StepNameRow>
                <Text variant="text-small" color="var(--text-secondary)">
                  Last run {lastRun ? formatRelativeTime(lastRun) : "never"}
                </Text>
              </StepInfoContainer>
            </StepRow>
          );
        })}
      </StepList>
    </WorkflowGroup>
  );
}

type UsageRowContent = {
  key: string;
  url: string;
  primary: React.ReactNode;
  secondary?: React.ReactNode;
};

/** A flat list of linked usage rows (chats, git remotes, audit destinations). */
function UsageListSection<T>({
  iconName,
  title,
  rows,
  renderRow,
  hiddenCount = 0,
  scopeKind,
}: {
  iconName: React.ComponentProps<typeof Icon>["name"];
  title: string;
  rows: readonly T[];
  renderRow: (row: T) => UsageRowContent | null;
  hiddenCount?: number;
  scopeKind?: string;
}) {
  return (
    <WorkflowSection>
      <WorkflowGroup>
        <WorkflowHeader>
          <Icon name={iconName} size={16} color="var(--text-primary)" />
          <Text variant="text-regular-md" color="var(--text-primary)" ellipsis>
            {title}
          </Text>
        </WorkflowHeader>
        <StepList>
          {rows.map((row) => {
            const content = renderRow(row);
            if (!content) return null;
            return (
              <StepRow key={content.key} to={content.url}>
                <StepColor />
                <StepInfoContainer>
                  <StepNameRow>{content.primary}</StepNameRow>
                  {content.secondary != null && (
                    <Text
                      variant="text-small"
                      color="var(--text-secondary)"
                      ellipsis
                    >
                      {content.secondary}
                    </Text>
                  )}
                </StepInfoContainer>
              </StepRow>
            );
          })}
          {scopeKind && (
            <RedactedScopeRow count={hiddenCount} scopeKind={scopeKind} />
          )}
        </StepList>
      </WorkflowGroup>
    </WorkflowSection>
  );
}

const Container = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const BranchPickerOpener = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  padding: 4px 8px;
  border-radius: 8px;
  transition: background-color var(--motion-fast) var(--ease-out);

  &:hover {
    background: var(--alpha4);
  }
`;
